import { SegmentEvents } from '@nile/mantine-theme/analytics'
import { emit } from './emit'

export type DashboardVariant = '1_0' | '1_5'
export type DashboardPage =
    | 'home'
    | 'my_care'
    | 'medications'
    | 'visits'
    | 'labs'
    | 'imaging'
    | 'orders'
    | 'updates'
    | 'care_plans'
    | 'care_plan_detail'
export type DashboardDwellExitReason = 'in_app_navigation' | 'page_exit'

const DASHBOARD_ROOT = '/account'

const PAGE_BY_SUFFIX: Record<string, DashboardPage> = {
    '': 'home',
    '/my-care': 'my_care',
    '/medications': 'medications',
    '/visits': 'visits',
    '/labs': 'labs',
    '/imaging': 'imaging',
    '/orders': 'orders',
    '/updates': 'updates',
    '/care-plans': 'care_plans',
}

/** Maps 1.5 dashboard surfaces. Profile, check-in, and unknown paths return null. */
export function dashboardPageFromPathname(pathname: string): DashboardPage | null {
    const normalized = pathname.replace(/\/$/, '') || '/'

    if (normalized === DASHBOARD_ROOT) return 'home'
    if (!normalized.startsWith(`${DASHBOARD_ROOT}/`)) return null

    const suffix = normalized.slice(DASHBOARD_ROOT.length)
    if (PAGE_BY_SUFFIX[suffix]) return PAGE_BY_SUFFIX[suffix]
    if (suffix.startsWith('/care-plans/')) return 'care_plan_detail'
    return null
}

export function emitDashboardViewed(args: { variant: DashboardVariant; page: DashboardPage }): void {
    if (args.variant === '1_5') {
        emit(SegmentEvents.DASHBOARD_1_5_VIEWED, { dashboard_page: args.page })
        return
    }
    emit(SegmentEvents.DASHBOARD_1_0_VIEWED)
}

function dwellEventName(variant: DashboardVariant): string {
    return variant === '1_5' ? SegmentEvents.DASHBOARD_1_5_DWELL : SegmentEvents.DASHBOARD_1_0_DWELL
}

interface DashboardDwellSession {
    variant: DashboardVariant
    startedAt: number
    isPageVisible: boolean
    isPageFocused: boolean
    activeMs: number
    activeStartMs: number | null
    flushed: boolean
}

let session: DashboardDwellSession | null = null

function now(): number {
    return typeof performance !== 'undefined' && typeof performance.now === 'function'
        ? performance.now()
        : Date.now()
}

function envActive(s: DashboardDwellSession): boolean {
    return s.isPageVisible && s.isPageFocused
}

function settleActiveClock(s: DashboardDwellSession, ts: number): void {
    if (s.activeStartMs == null) return
    s.activeMs += Math.max(0, ts - s.activeStartMs)
    s.activeStartMs = null
}

function reconcileEnv(s: DashboardDwellSession, ts: number): void {
    if (envActive(s)) {
        if (s.activeStartMs == null) s.activeStartMs = ts
    } else {
        settleActiveClock(s, ts)
    }
}

/** Starts the dashboard-visit clock. No-op if a session is already open. */
export function beginDashboardDwellSession(variant: DashboardVariant): void {
    if (session && !session.flushed) return

    const startedAt = now()
    const isPageVisible = typeof document === 'undefined' ? true : document.visibilityState === 'visible'
    const isPageFocused = typeof document === 'undefined' ? true : document.hasFocus()

    session = {
        variant,
        startedAt,
        isPageVisible,
        isPageFocused,
        activeMs: 0,
        activeStartMs: isPageVisible && isPageFocused ? startedAt : null,
        flushed: false,
    }
}

export function setDashboardPageVisibility(isVisible: boolean): void {
    if (!session || session.isPageVisible === isVisible) return
    session.isPageVisible = isVisible
    reconcileEnv(session, now())
}

export function setDashboardPageFocus(isFocused: boolean): void {
    if (!session || session.isPageFocused === isFocused) return
    session.isPageFocused = isFocused
    reconcileEnv(session, now())
}

export function flushDashboardDwellSession(exitReason: DashboardDwellExitReason): void {
    if (!session || session.flushed) return
    const ts = now()
    settleActiveClock(session, ts)

    const totalMs = Math.round(ts - session.startedAt)
    // React 18 Strict Mode: mount → cleanup flush with no real visit.
    const looksLikeStrictModeNoise = exitReason === 'in_app_navigation' && totalMs < 500
    session.flushed = true
    if (looksLikeStrictModeNoise) return

    emit(dwellEventName(session.variant), {
        total_ms_on_page: totalMs,
        active_ms_on_page: Math.round(session.activeMs),
        exit_reason: exitReason,
    })
}

export function endDashboardDwellSession(): void {
    session = null
}

/** Tests only. */
export function _peekDashboardDwellSessionForTests(): DashboardDwellSession | null {
    return session
}
